import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { CrackedLogo } from "../components/CrackedLogo";
import { 
  UserPlus, 
  Trash2, 
  LogOut,
  Users,
  Shield,
  Mail,
  User,
  CheckCircle2,
  AlertCircle,
  XCircle,
  HelpCircle,
  RefreshCw,
  Activity,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [allowedInterviewers, setAllowedInterviewers] = useState([]);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [healthRows, setHealthRows] = useState([]);
  const [healthLoading, setHealthLoading] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [attemptsSummary, setAttemptsSummary] = useState({});
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [attemptsFilter, setAttemptsFilter] = useState('all');
  const [attemptsExpanded, setAttemptsExpanded] = useState(true);

  const fetchHealth = useCallback(async (userId) => {
    setHealthLoading(true);
    try {
      const res = await axios.get(`${API}/admin/interviewers-health?user_id=${userId}`);
      setHealthRows(res.data.interviewers || []);
    } catch (error) {
      // Silent — health is auxiliary; main dashboard still loads
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const fetchAttempts = useCallback(async (userId, outcome) => {
    setAttemptsLoading(true);
    try {
      const q = outcome && outcome !== 'all' ? `&outcome=${outcome}` : '';
      const res = await axios.get(`${API}/admin/booking-attempts?user_id=${userId}&limit=100${q}`);
      setAttempts(res.data.attempts || []);
      setAttemptsSummary(res.data.summary || {});
    } catch (error) {
      // Silent — auxiliary
    } finally {
      setAttemptsLoading(false);
    }
  }, []);

  // Refetch attempts whenever the filter changes
  useEffect(() => {
    const userId = localStorage.getItem('cracked_user_id');
    if (userId && user) {
      fetchAttempts(userId, attemptsFilter);
    }
  }, [attemptsFilter, user, fetchAttempts]);

  const fetchData = useCallback(async (userId) => {
    try {
      const [userRes, interviewersRes] = await Promise.all([
        axios.get(`${API}/users/me?user_id=${userId}`),
        axios.get(`${API}/admin/allowed-interviewers?user_id=${userId}`)
      ]);
      
      setUser(userRes.data);
      setAllowedInterviewers(interviewersRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate('/');
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    let userId = searchParams.get('user_id');
    if (userId) {
      localStorage.setItem('cracked_user_id', userId);
    } else {
      userId = localStorage.getItem('cracked_user_id');
    }

    if (!userId) {
      navigate('/');
      return;
    }

    fetchData(userId);
    fetchHealth(userId);
    fetchAttempts(userId, 'all');
  }, [searchParams, navigate, fetchData, fetchHealth, fetchAttempts]);

  const handleLogout = async () => {
    try {
      const userId = localStorage.getItem('cracked_user_id');
      await axios.get(`${API}/auth/logout?user_id=${userId}`);
      localStorage.removeItem('cracked_user_id');
      navigate('/');
    } catch (error) {
      localStorage.removeItem('cracked_user_id');
      navigate('/');
    }
  };

  const handleAddInterviewer = async (e) => {
    e.preventDefault();
    
    if (!newName.trim() || !newEmail.trim()) {
      toast.error("Please enter both name and email");
      return;
    }

    if (!newEmail.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }

    setAdding(true);
    try {
      const userId = localStorage.getItem('cracked_user_id');
      await axios.post(`${API}/admin/allowed-interviewers?user_id=${userId}`, {
        name: newName.trim(),
        email: newEmail.trim().toLowerCase()
      });
      
      toast.success(`Added ${newName} to allowed interviewers`);
      setAllowedInterviewers([...allowedInterviewers, { 
        name: newName.trim(), 
        email: newEmail.trim().toLowerCase() 
      }]);
      setNewName('');
      setNewEmail('');
      fetchHealth(userId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add interviewer");
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveInterviewer = async (email) => {
    try {
      const userId = localStorage.getItem('cracked_user_id');
      await axios.delete(`${API}/admin/allowed-interviewers/${encodeURIComponent(email)}?user_id=${userId}`);
      
      setAllowedInterviewers(allowedInterviewers.filter(i => i.email !== email));
      toast.success("Interviewer removed from allowed list");
      fetchHealth(userId);
    } catch (error) {
      toast.error("Failed to remove interviewer");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-cracked-deep-teal text-white px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CrackedLogo inverted />
            <div className="hidden sm:flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full">
              <Shield className="w-4 h-4" />
              <span className="text-sm font-medium">Admin</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              {user?.picture && (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              )}
              <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-cracked-deep-teal mb-2">Admin Dashboard</h1>
          <p className="text-cracked-deep-teal/60">Manage who can sign up as an interviewer</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Add Interviewer Form */}
          <Card data-testid="add-interviewer-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-cracked-deep-teal">
                <UserPlus className="w-5 h-5" />
                Add Allowed Interviewer
              </CardTitle>
              <CardDescription>
                Add organization members who can sign up as interviewers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddInterviewer} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    placeholder="John Doe"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    data-testid="interviewer-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@crack-ed.com"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    data-testid="interviewer-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={adding || !newName.trim() || !newEmail.trim()}
                  className="w-full bg-cracked-light-teal hover:bg-cracked-deep-teal text-white"
                  data-testid="add-interviewer-btn"
                >
                  {adding ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Interviewer
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Allowed Interviewers List */}
          <Card data-testid="allowed-interviewers-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-cracked-deep-teal">
                <Users className="w-5 h-5" />
                Allowed Interviewers ({allowedInterviewers.length})
              </CardTitle>
              <CardDescription>
                People who can sign up as interviewers
              </CardDescription>
            </CardHeader>
            <CardContent>
              {allowedInterviewers.length === 0 ? (
                <div className="text-center py-8 text-cracked-deep-teal/50">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No allowed interviewers yet</p>
                  <p className="text-sm">Add organization members who can be interviewers</p>
                </div>
              ) : (
                <ScrollArea className="h-[300px]">
                  <div className="space-y-3">
                    {allowedInterviewers.map((interviewer, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100"
                        data-testid={`interviewer-${index}`}
                      >
                        <div>
                          <p className="font-medium text-cracked-deep-teal">{interviewer.name}</p>
                          <p className="text-sm text-cracked-deep-teal/60">{interviewer.email}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveInterviewer(interviewer.email)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`remove-interviewer-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Calendar Connection Status */}
        <Card className="mt-8" data-testid="calendar-health-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-cracked-deep-teal">
                  <Shield className="w-5 h-5" />
                  Calendar Connection Status
                </CardTitle>
                <CardDescription>
                  Live check of each interviewer&apos;s Google Calendar connection. Red rows cannot receive bookings.
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const userId = localStorage.getItem('cracked_user_id');
                  if (userId) fetchHealth(userId);
                }}
                disabled={healthLoading}
                className="text-cracked-deep-teal hover:bg-cracked-light-teal/10"
                data-testid="refresh-health-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${healthLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {healthLoading && healthRows.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="spinner" />
              </div>
            ) : healthRows.length === 0 ? (
              <div className="text-center py-8 text-cracked-deep-teal/50">
                <p>No interviewers to check</p>
              </div>
            ) : (
              <div className="space-y-2">
                {healthRows.map((row, index) => {
                  const config = {
                    healthy: {
                      Icon: CheckCircle2,
                      iconClass: "text-green-600",
                      label: "Connected",
                      pillClass: "bg-green-50 text-green-700 border-green-200",
                    },
                    not_signed_in: {
                      Icon: HelpCircle,
                      iconClass: "text-slate-400",
                      label: "Not signed in",
                      pillClass: "bg-slate-50 text-slate-600 border-slate-200",
                    },
                    no_tokens: {
                      Icon: AlertCircle,
                      iconClass: "text-amber-600",
                      label: "Needs re-login",
                      pillClass: "bg-amber-50 text-amber-700 border-amber-200",
                    },
                    missing_refresh: {
                      Icon: XCircle,
                      iconClass: "text-red-600",
                      label: "Re-auth required",
                      pillClass: "bg-red-50 text-red-700 border-red-200",
                    },
                    missing_scope: {
                      Icon: XCircle,
                      iconClass: "text-red-600",
                      label: "Calendar scope missing",
                      pillClass: "bg-red-50 text-red-700 border-red-200",
                    },
                    refresh_failed: {
                      Icon: XCircle,
                      iconClass: "text-red-600",
                      label: "Token revoked",
                      pillClass: "bg-red-50 text-red-700 border-red-200",
                    },
                  }[row.status] || {
                    Icon: HelpCircle,
                    iconClass: "text-slate-400",
                    label: row.status,
                    pillClass: "bg-slate-50 text-slate-600 border-slate-200",
                  };

                  const { Icon } = config;

                  return (
                    <div
                      key={row.email}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-100 bg-slate-50"
                      data-testid={`health-row-${index}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Icon className={`w-5 h-5 flex-shrink-0 ${config.iconClass}`} />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-cracked-deep-teal truncate">{row.name || row.email}</p>
                          <p className="text-xs text-cracked-deep-teal/60 truncate">{row.email}</p>
                          <p className="text-xs text-cracked-deep-teal/50 mt-0.5">{row.message}</p>
                        </div>
                      </div>
                      <span
                        className={`text-xs px-2.5 py-1 rounded-full border whitespace-nowrap ${config.pillClass}`}
                        data-testid={`health-status-${index}`}
                      >
                        {config.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Booking Attempts Log */}
        <Card className="mt-8" data-testid="booking-attempts-card">
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <CardTitle className="flex items-center gap-2 text-cracked-deep-teal">
                  <Activity className="w-5 h-5" />
                  Booking Attempts Log
                </CardTitle>
                <CardDescription>
                  Every booking attempt — successful and blocked — with reason. Last 100 entries.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const userId = localStorage.getItem('cracked_user_id');
                    if (userId) fetchAttempts(userId, attemptsFilter);
                  }}
                  disabled={attemptsLoading}
                  className="text-cracked-deep-teal hover:bg-cracked-light-teal/10"
                  data-testid="refresh-attempts-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${attemptsLoading ? 'animate-spin' : ''}`} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAttemptsExpanded(v => !v)}
                  className="text-cracked-deep-teal hover:bg-cracked-light-teal/10"
                  data-testid="toggle-attempts-btn"
                >
                  {attemptsExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Filter chips */}
            {attemptsExpanded && (
              <div className="flex flex-wrap gap-2 mt-3">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'success', label: 'Success' },
                  { key: 'lead_not_in_crm', label: 'Lead not in CRM' },
                  { key: 'wrong_counsellor', label: 'Wrong counsellor' },
                  { key: 'calendar_not_connected', label: 'Calendar not connected' },
                  { key: 'calendar_api_failed', label: 'Calendar API failed' },
                  { key: 'slot_taken', label: 'Slot taken' },
                ].map((f) => {
                  const isActive = attemptsFilter === f.key;
                  const count = f.key === 'all'
                    ? Object.values(attemptsSummary).reduce((a, b) => a + b, 0)
                    : (attemptsSummary[f.key] || 0);
                  return (
                    <button
                      key={f.key}
                      onClick={() => setAttemptsFilter(f.key)}
                      data-testid={`attempts-filter-${f.key}`}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                        isActive
                          ? 'bg-cracked-light-teal text-white border-cracked-light-teal'
                          : 'bg-white text-cracked-deep-teal/70 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {f.label}
                      <span className={`ml-1.5 ${isActive ? 'text-white/80' : 'text-cracked-deep-teal/40'}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </CardHeader>

          {attemptsExpanded && (
            <CardContent>
              {attemptsLoading && attempts.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <div className="spinner" />
                </div>
              ) : attempts.length === 0 ? (
                <div className="text-center py-8 text-cracked-deep-teal/50">
                  <Activity className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No booking attempts yet</p>
                </div>
              ) : (
                <ScrollArea className="h-[420px]">
                  <div className="space-y-2">
                    {attempts.map((a, index) => {
                      const isSuccess = a.outcome === 'success';
                      const ts = a.timestamp ? new Date(a.timestamp) : null;
                      const tsStr = ts
                        ? ts.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'short', timeStyle: 'short' })
                        : '—';
                      const slot = a.slot_start
                        ? new Date(a.slot_start).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'short', timeStyle: 'short' })
                        : '—';

                      return (
                        <div
                          key={a.id || index}
                          className={`p-3 rounded-lg border ${
                            isSuccess
                              ? 'bg-green-50/50 border-green-100'
                              : 'bg-red-50/40 border-red-100'
                          }`}
                          data-testid={`attempt-row-${index}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2 min-w-0 flex-1">
                              {isSuccess
                                ? <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                                : <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                              }
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap text-xs text-cracked-deep-teal/60">
                                  <span>{tsStr} IST</span>
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                    isSuccess
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}>
                                    {a.outcome}
                                  </span>
                                </div>
                                <p className="text-sm text-cracked-deep-teal mt-1">
                                  <span className="font-medium">{a.counsellor_name || a.counsellor_email || 'Unknown counsellor'}</span>
                                  {' → '}
                                  <span className="font-medium">{a.interviewer_name || a.interviewer_email || 'Unknown interviewer'}</span>
                                </p>
                                <div className="text-xs text-cracked-deep-teal/70 mt-1 space-y-0.5">
                                  {a.interviewee_email && (
                                    <p>Candidate: <span className="font-mono">{a.interviewee_email}</span></p>
                                  )}
                                  <p>Slot: {slot}</p>
                                  {!isSuccess && a.error_message && (
                                    <p className="text-red-700/80 italic">{a.error_message}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          )}
        </Card>
      </main>
    </div>
  );
}
